/**
 * Lightweight runtime validation of the /ws/client protocol (no zod in the bundle).
 * Every function returns a list of problems; an empty list means "valid".
 */
import {
  CLIENT_MESSAGE_TYPES,
  NET_KINDS,
  NET_VIAS,
  SERVER_MESSAGE_TYPES,
  type ClientToServer,
  type ServerToClient,
} from './protocol';

type Obj = Record<string, unknown>;

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isInt = (v: unknown): v is number => isNum(v) && Number.isInteger(v);
const isStr = (v: unknown): v is string => typeof v === 'string';
const oneOf = (v: unknown, values: readonly string[]) => isStr(v) && values.includes(v);

class Checker {
  readonly errors: string[] = [];
  constructor(private readonly o: Obj) {}
  private fail(path: string, what: string) {
    this.errors.push(`${path}: ${what}`);
  }
  num(k: string) {
    if (!isNum(this.o[k])) this.fail(k, 'number expected');
    return this;
  }
  int(k: string) {
    if (!isInt(this.o[k])) this.fail(k, 'integer expected');
    return this;
  }
  str(k: string, nonEmpty = false) {
    const v = this.o[k];
    if (!isStr(v) || (nonEmpty && v.length === 0)) this.fail(k, 'string expected');
    return this;
  }
  uuid(k: string) {
    const v = this.o[k];
    if (!isStr(v) || !UUID_RE.test(v)) this.fail(k, 'uuid expected');
    return this;
  }
  bool(k: string) {
    if (typeof this.o[k] !== 'boolean') this.fail(k, 'boolean expected');
    return this;
  }
  enumOf(k: string, values: readonly string[], optional = false) {
    const v = this.o[k];
    if (optional && v === undefined) return this;
    if (!oneOf(v, values)) this.fail(k, `one of ${values.join('|')} expected`);
    return this;
  }
  nullableStr(k: string, optional = false) {
    const v = this.o[k];
    if (optional && v === undefined) return this;
    if (v !== null && !isStr(v)) this.fail(k, 'string|null expected');
    return this;
  }
  scope(k: string, nullable: boolean) {
    const v = this.o[k];
    if (nullable && v === null) return this;
    if (!isObj(v) || !oneOf(v.kind, ['server', 'match']) || !isStr(v.label)) this.fail(k, 'scope expected');
    return this;
  }
  custom(ok: boolean, path: string, what: string) {
    if (!ok) this.fail(path, what);
    return this;
  }
}

const ROLES = ['PLAYER', 'SPECTATOR'] as const;

export function validateServerMessage(v: unknown): string[] {
  if (!isObj(v)) return ['message: object expected'];
  if (!oneOf(v.type, SERVER_MESSAGE_TYPES)) return [`type: unknown server message type ${String(v.type)}`];
  const c = new Checker(v);
  switch (v.type as ServerToClient['type']) {
    case 'hello': {
      c.uuid('uuid').str('name', true).int('serverTime');
      const cfg = v.config;
      const ok =
        isObj(cfg) &&
        isNum(cfg.spatialHz) &&
        isNum(cfg.interpDelayMs) &&
        isObj(cfg.gain) &&
        isNum(cfg.gain.refDistance) &&
        isNum(cfg.gain.hearRadius) &&
        isNum(cfg.gain.rolloffExponent);
      c.custom(ok, 'config', 'config {spatialHz, interpDelayMs, gain{refDistance,hearRadius,rolloffExponent}} expected');
      break;
    }
    case 'state':
      c.enumOf('mc', ['online', 'offline']).nullableStr('server').scope('scope', true);
      c.enumOf('role', ROLES, true).nullableStr('teamId', true);
      break;
    case 'room.assign':
      c.int('epoch').str('room', true).str('livekitUrl', true).str('token', true).int('expiresAt').scope('scope', false);
      c.custom(isObj(v.audio) && oneOf(v.audio.mode, ['spatial', 'global']), 'audio', 'audio {mode} expected');
      break;
    case 'room.leave':
      c.int('epoch').enumOf('reason', ['MC_OFFLINE', 'REVOKED', 'SERVER_DOWN', 'NO_SCOPE']);
      break;
    case 'token.issued':
      c.int('epoch').str('token', true).int('expiresAt');
      break;
    case 'peers': {
      c.int('epoch');
      const peers = v.peers;
      if (!Array.isArray(peers)) {
        c.custom(false, 'peers', 'array expected');
        break;
      }
      peers.forEach((p, i) => {
        if (!isObj(p)) {
          c.custom(false, `peers[${i}]`, 'object expected');
          return;
        }
        const pc = new Checker(p).uuid('identity').str('name', true).enumOf('role', ROLES).bool('radio');
        pc.nullableStr('teamId', true).enumOf('net', NET_KINDS, true).enumOf('via', NET_VIAS, true);
        pc.errors.forEach((e) => c.errors.push(`peers[${i}].${e}`));
      });
      break;
    }
    case 'permissions': {
      c.int('epoch');
      const allow = v.allow;
      c.custom(Array.isArray(allow) && allow.every((id) => isStr(id) && UUID_RE.test(id)), 'allow', 'array of uuid expected');
      break;
    }
    case 'spatial': {
      c.int('epoch').int('t');
      c.custom(isObj(v.self) && isNum(v.self.yaw) && isNum(v.self.pitch), 'self', '{yaw,pitch} expected');
      const p = v.p;
      if (!Array.isArray(p)) {
        c.custom(false, 'p', 'array expected');
        break;
      }
      p.forEach((t, i) => {
        const ok =
          Array.isArray(t) &&
          t.length === 5 &&
          isStr(t[0]) &&
          UUID_RE.test(t[0]) &&
          isNum(t[1]) &&
          isNum(t[2]) &&
          isNum(t[3]) &&
          isInt(t[4]) &&
          (t[4] as number) >= 0;
        c.custom(ok, `p[${i}]`, '[identity, dx, dy, dz, flags] expected');
      });
      break;
    }
    case 'kick':
      c.enumOf('reason', ['OTHER_TAB', 'UNLINKED', 'SESSION_EXPIRED']);
      break;
    case 'error':
      c.str('code', true).str('message');
      break;
    case 'pong':
      c.int('n');
      break;
  }
  return c.errors;
}

const CLIENT_KEYS: Record<ClientToServer['type'], readonly string[]> = {
  'room.joined': ['type', 'epoch', 'room'],
  'room.failed': ['type', 'epoch', 'room', 'error'],
  'token.request': ['type', 'epoch'],
  prefs: ['type', 'micMode'],
  ping: ['type', 'n'],
  net: ['type', 'epoch', 'net', 'via'],
  mic: ['type', 'muted', 'deafened'],
};

/** Validates a client -> server message, strictly (no extra keys: the central closes with 4400). */
export function validateClientMessage(v: unknown): string[] {
  if (!isObj(v)) return ['message: object expected'];
  if (!oneOf(v.type, CLIENT_MESSAGE_TYPES)) return [`type: unknown client message type ${String(v.type)}`];
  const type = v.type as ClientToServer['type'];
  const c = new Checker(v);
  for (const k of Object.keys(v)) if (!CLIENT_KEYS[type].includes(k)) c.custom(false, k, 'unexpected key');
  switch (type) {
    case 'room.joined':
      c.int('epoch').str('room', true);
      break;
    case 'room.failed':
      c.int('epoch').str('room', true).str('error');
      break;
    case 'token.request':
      c.int('epoch');
      break;
    case 'prefs':
      c.enumOf('micMode', ['vad', 'ptt']);
      break;
    case 'ping':
      c.int('n');
      break;
    case 'net':
      c.int('epoch').enumOf('net', NET_KINDS).enumOf('via', NET_VIAS);
      c.custom((v.net === 'direct') === (v.via === 'udp'), 'via', '`direct` goes with `udp` only');
      break;
    case 'mic':
      c.bool('muted').bool('deafened');
      break;
  }
  return c.errors;
}

/** Parses one raw frame. Returns null for invalid JSON or invalid/unknown messages. */
export function parseServerMessage(raw: string): ServerToClient | null {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  // Tolerate an accidental `{type, payload:{...}}` envelope by flattening it.
  if (isObj(json) && isObj(json.payload) && Object.keys(json).every((k) => k === 'type' || k === 'payload' || k === 'v' || k === 'id' || k === 'ts')) {
    json = { type: json.type, ...json.payload };
  }
  return validateServerMessage(json).length === 0 ? (json as ServerToClient) : null;
}

export type RestBodyName =
  | 'link.code.request'
  | 'link.response'
  | 'me.response'
  | 'config.response'
  | 'error.response';

export const REST_BODY_NAMES: readonly RestBodyName[] = [
  'link.code.request',
  'link.response',
  'me.response',
  'config.response',
  'error.response',
];

/** Validates a REST body of §6.1 (as typed in protocol.ts). */
export function validateRestBody(name: RestBodyName, v: unknown): string[] {
  if (!isObj(v)) return ['body: object expected'];
  const c = new Checker(v);
  switch (name) {
    case 'link.code.request':
      c.str('code', true);
      break;
    case 'link.response':
      c.uuid('uuid').str('name', true);
      break;
    case 'me.response':
      c.uuid('uuid').str('name', true).bool('mcOnline').nullableStr('server').num('linkedAt');
      break;
    case 'config.response': {
      c.str('livekitUrl', true).str('version');
      const d = v.spatialDefaults;
      const ok =
        isObj(d) &&
        isNum(d.refDistance) &&
        isNum(d.hearRadius) &&
        isNum(d.rolloffExponent) &&
        isNum(d.spatialHz) &&
        isNum(d.interpDelayMs) &&
        (d.usePitch === undefined || typeof d.usePitch === 'boolean');
      c.custom(ok, 'spatialDefaults', 'spatial defaults expected');
      break;
    }
    case 'error.response':
      c.str('error', true);
      if (v.message !== undefined) c.str('message');
      break;
  }
  return c.errors;
}
